import { usePostOfficeContext } from '@atlassian/post-office-context';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { AdControls } from '@post-office/components--ad-controls';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import type { ClickedElementAttributes } from '@post-office/message-template-contracts/message-instance-event';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useEffect, useMemo } from 'react';

import {
	getEnvTypeFromHostname,
	getRovoOnboardingClickUrl,
} from '../../../../../common/rovo-onboarding-url';
import { ROVO_ICON } from '../../assets/rovo-icon';
import { type JSX } from 'react/jsx-runtime';

const i18n = defineMessages({
	headingLabel: {
		id: 'post-office.confluence-rovo-onboarding-banner.heading',
		defaultMessage: 'Rovo is ready for you',
		description: 'Heading for the Rovo onboarding banner.',
	},
	bodyLabel: {
		id: 'post-office.confluence-rovo-onboarding-banner.body',
		defaultMessage:
			"Power up your workspace. Set up and configure connectors to unlock all Rovo's potential.",
		description: 'Body text for the Rovo onboarding banner.',
	},
	callToActionLabel: {
		id: 'post-office.confluence-rovo-onboarding-banner.call.to.action',
		defaultMessage: 'Get started',
		description: 'Call to action button on the Rovo onboarding banner.',
	},
	whyAmISeeingThisModalMessage: {
		id: 'post-office.confluence-rovo-onboarding-banner.why-am-i-seeing-this.message',
		defaultMessage:
			"We think setting up Rovo and connecting apps will improve your team's productivity. You're seeing this because Rovo is available to your team, and as an organizational admin you can extend Rovo's features by configuring connectors.",
		description: 'Message explaining why the user is seeing this banner.',
	},
	whyAmISeeingThisModalReason: {
		id: 'post-office.confluence-rovo-onboarding-banner.why-am-i-seeing-this.reason',
		defaultMessage: 'Rovo is available to your team and you are an organizational admin',
		description: 'Reason for showing the banner.',
	},
});

const styles = cssMap({
	root: {
		position: 'relative',
		flexGrow: 1,
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
	},
	primaryContentContainer: {
		flexGrow: 1,
		padding: token('space.200'),
	},
	headingContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	iconContainer: {
		flexShrink: 0,
		width: '32px',
		height: '32px',
	},
	ctaWrapper: {
		marginTop: token('space.050'),
	},
	adControlsContainer: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
});

type RovoOnboardingBannerMessageInstanceClickedParams = {
	actionType: 'open' | 'dismiss';
	clickedElement: ClickedElementAttributes;
};

export type RovoOnboardingBannerProps = {
	action: NextBestAction;
	/** Org ID for the Rovo admin URL; set by implicit message creator (from context or TCS). Optional when triggering manually. */
	orgId?: string;
	onPrimaryActionClick?: (nextBestActionType: NextBestAction) => string | void | null;
	onDismissClick?: () => void;
};

export const RovoOnboardingBanner = ({
	action,
	orgId: orgIdFromMessage,
	onPrimaryActionClick,
	onDismissClick,
}: RovoOnboardingBannerProps): JSX.Element => {
	const postOfficeContext = usePostOfficeContext();
	// Use orgId from message context first; fallback to product/placement context (e.g. when message payload omits it but event context has it)
	const orgId =
		orgIdFromMessage ??
		(typeof postOfficeContext?.orgId === 'string' ? postOfficeContext.orgId : undefined);

	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageInstanceClicked, messageInstanceViewed } = useMessageInstanceEvent();

	useEffect(() => {
		if (!messageInstanceViewed) {
			return;
		}

		void messageInstanceViewed();
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'ConfluenceRovoOnboardingBanner',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fireMessageInstanceClicked = ({
		actionType,
		clickedElement,
	}: RovoOnboardingBannerMessageInstanceClickedParams) => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: clickedElement,
			action: {
				actionType: actionType,
			},
			additionalMetadataFromFrontend: {},
		});
	};

	const rovoAdminUrl = useMemo(() => {
		if (!globalThis.location) {
			return '#';
		}
		const envType = getEnvTypeFromHostname(globalThis.location.hostname);
		return getRovoOnboardingClickUrl(orgId, envType);
	}, [orgId]);

	const handleOnPrimaryActionClick = () => {
		void fireMessageInstanceClicked({
			actionType: 'open',
			clickedElement: {
				clickIdentifier: 'getStarted',
				clickedElement: 'button',
				buttonType: 'primary',
			},
		});

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoOnboardingBannerCta',
		});

		void onPrimaryActionClick?.(action);

		if (rovoAdminUrl && rovoAdminUrl !== '#' && globalThis.location) {
			globalThis.location.href = rovoAdminUrl;
		}
	};

	const handleOnDismissed = (_dismissedMessageInstanceId: string) => {
		void onDismissClick?.();
	};

	return (
		<AnalyticsContext data={{ source: 'confluence-rovo-onboarding-banner' }}>
			<Flex xcss={styles.root} testId="confluence-rovo-onboarding-banner">
				<Flex xcss={styles.primaryContentContainer} gap="space.100" direction="column">
					<Flex xcss={styles.headingContainer}>
						<Box xcss={styles.iconContainer}>
							<ROVO_ICON />
						</Box>
						<Heading size="small">
							<FormattedMessage {...i18n.headingLabel} />
						</Heading>
					</Flex>
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...i18n.bodyLabel} />
					</Text>
					<Box xcss={styles.ctaWrapper}>
						<LinkButton
							href={rovoAdminUrl}
							onClick={handleOnPrimaryActionClick}
							spacing="compact"
							target="_self"
						>
							<FormattedMessage {...i18n.callToActionLabel} />
						</LinkButton>
					</Box>
				</Flex>
				<Box xcss={styles.adControlsContainer}>
					<AdControls
						iconSize="small"
						spacing="compact"
						onDismissed={handleOnDismissed}
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							message: <FormattedMessage {...i18n.whyAmISeeingThisModalMessage} />,
							reasons: [<FormattedMessage key="reason" {...i18n.whyAmISeeingThisModalReason} />],
						}}
					/>
				</Box>
			</Flex>
		</AnalyticsContext>
	);
};
